import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, Share2, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { PropertyCard } from "@/components/docstate/PropertyCard";
import { useShortlist } from "@/context/ShortlistContext";
import { reDetail, dl } from "@/lib/docstate";

export default function DocstateShortlist() {
  const { items, clear } = useShortlist();
  const [shared, setShared] = useState(null); // listings loaded from a shared ?ids link
  const [loadingShared, setLoadingShared] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    const ids = new URLSearchParams(window.location.search).get("ids");
    if (ids) {
      setLoadingShared(true);
      Promise.all(ids.split(",").filter(Boolean).map((id) => reDetail(id).catch(() => null)))
        .then((res) => setShared(res.filter(Boolean)))
        .finally(() => setLoadingShared(false));
    }
  }, []);

  const list = shared || items;
  const isShared = !!shared;

  const share = async () => {
    const url = `${window.location.origin}${dl("/shortlist")}?ids=${items.map((i) => i.reference || i.id).join(",")}`;
    try {
      if (navigator.share) await navigator.share({ title: "My Docstate shortlist", url });
      else { await navigator.clipboard.writeText(url); toast.success("Shortlist link copied to clipboard"); }
    } catch { /* user dismissed share sheet */ }
  };

  return (
    <div className="ds-bg min-h-screen pt-16" data-testid="docstate-shortlist">
      <div className="mx-auto max-w-7xl px-4 py-14 sm:px-8 lg:px-12">
        <p className="ds-eyebrow">{isShared ? "A shared selection" : "Your shortlist"}</p>
        <div className="mt-2 flex flex-wrap items-end justify-between gap-4">
          <h1 className="ds-display text-4xl font-medium text-white sm:text-5xl">{isShared ? "Shared Properties" : "Saved Properties"}</h1>
          {!isShared && items.length > 0 && (
            <div className="flex gap-2">
              <button onClick={share} className="ds-gold-btn px-5 py-2.5 text-sm" data-testid="shortlist-share"><Share2 className="h-4 w-4" /> Share</button>
              <button onClick={clear} className="ds-outline-btn px-5 py-2.5 text-sm" data-testid="shortlist-clear"><Trash2 className="h-4 w-4" /> Clear</button>
            </div>
          )}
        </div>

        {loadingShared ? (
          <div className="mt-16 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#C9A24B]" /></div>
        ) : list.length ? (
          <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 lg:gap-8">
            {list.map((l, i) => <PropertyCard key={l.id} listing={l} index={i} />)}
          </div>
        ) : (
          <div className="ds-card mt-10 py-20 text-center" data-testid="shortlist-empty">
            <Heart className="mx-auto h-10 w-10 text-[#C9A24B]/60" />
            <p className="ds-display mt-4 text-xl text-[#F3E5AB]">No saved properties yet</p>
            <p className="mt-2 text-sm font-light text-[#9D8D83]">Tap the heart on any property to save it here and share with family or your agent.</p>
            <Link to={dl("/properties")} className="ds-gold-btn mt-6 inline-flex px-6 py-3 text-sm">Browse properties</Link>
          </div>
        )}
      </div>
    </div>
  );
}
